import React, { useContext } from 'react'
import { startOfDay, differenceInDays, add } from 'date-fns'
import Error from 'next/error'
import dynamic from 'next/dynamic'

import Layout from '../../../components/layout'
import ContentArea from '../../../components/content-area'
import Header from '../../../components/header'
import Footer from '../../../components/footer'
import GroupDivider from '../../../components/group-divider'
import Stat from '../../../components/stat'
import ChallengeDetails from '../../../components/challenge-details'
import ChallengeHeader from '../../../components/challenge-header'
import JoinContainer from '../../../components/join-container'
import BackfillFlag from '../../../components/backfill-flag'

import Participants from '../../../components/participants'

import { UserContext } from '../../../contexts/user-context'
import useHasMounted from '../../../hooks/use-has-mounted'

const Map = dynamic(() => import('../../../components/map-v2'), { ssr: false })
const PointDetails = dynamic(() => import('../../../components/point-details'))

const ChallengeJoin = ({ challenge, coupons, error }) => {
  const hasMounted = useHasMounted()
  const userHook = useContext(UserContext)
  const user = userHook.user
  
  let map
  if (challenge && challenge.map && challenge.map.waypoints) {
    map = challenge.map
  } else if (challenge && challenge.group.map && challenge.group.map.waypoints) {
    map = challenge.group.map
  }

  if (!challenge) return <Error statusCode={404} />

  const daysUntilStart = differenceInDays(add(new Date(challenge.start), { days: 1 }), startOfDay(new Date()))
  
  return (
    <Layout
      title={`${challenge.challengeName} - (powered by Challenge Hound)`}
      description={challenge.challengeDesc}
      keywords={challenge.challengeDesc}
      image={challenge.logo}
      noRobots={challenge.publish}>
      <Header section={user?.id ? '' : 'public'} subsection='join' />
      <main className={challenge.theme}>
        <ContentArea>
          <GroupDivider group={challenge.group} />
          
          {hasMounted && <>
            <ChallengeHeader user={user} challenge={challenge} desc page='join' />
            
            <div className='grid grid-cols-1 md:grid-cols-2 gap-2 mb-12'>
              <div>
                <ChallengeDetails challenge={challenge} imperial={user ? user.imperial : null} />
              </div>
              <div>
                {Boolean(challenge.dayOfChallenge) && 
                  <div className='grid grid-cols-2 gap-2'>
                    <Stat value={challenge.dayOfChallenge} label='Days into Challenge' />
                    <Stat value={challenge.daysLeft} label='Days Remaining' />
                  </div>
                }
                {!Boolean(challenge.dayOfChallenge) && 
                  <Stat value={daysUntilStart} label='Days Until Challenge Starts' />
                }
                {(Boolean(challenge.dayOfChallenge) && !Boolean(challenge.backfill)) && <BackfillFlag />}
              </div>
            </div>

            {challenge.measure === 'pointGoal' && <div className='mb-14 '>
              <PointDetails challenge={challenge} />
            </div>}

            <div className='mb-12 mt-18'>
              <JoinContainer user={user} challenge={challenge} coupons={coupons} />
            </div>

            {(map && map.waypoints) && <div className='bg-chLight w-full mb-8 h-[350px] md:h-[500px]'>
              <Map map={map} challengeDistance={challenge.distance} progressPoints={[]} user={user} />
            </div>}
              
            <Participants user={user} challenge={challenge} />
          </>}

        </ContentArea>
      </main>
      <Footer />
    </Layout>
  );
}

const fetchData = async (url) => {
  const getConfig = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',

    }
  }
  const res = await fetch(url, getConfig)
  if (!res.ok) {
    const error = await res.text()
    console.error(error)
    throw error
  }
  return res.json()
}

export async function getServerSideProps(context) {
  const challengeId = context.query.id

  let challenge
  let coupons
  try {
    challenge = await fetchData(`${process.env.API}/challenges/${challengeId}`)
    if (challenge && challenge.id) {
      coupons = await fetchData(`${process.env.API}/coupons/${challenge.group.organizationId}/${challenge.id}`)
    }
  } catch (error) {
    console.error(error)
    return { props: { error }}
  }

  return {
    props: {
      challenge,
      coupons
    }
  }
}

export default ChallengeJoin
